import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./QuestionForum.css";

const isEmptyHtml = (html) => {
  if (!html) return true;
  const clean = html.replace(/<[^>]*>/g, "").trim();
  return clean === "";
};

/**
 * Normalizes Firestore data with flat dotted field paths (legacy format)
 * into proper nested objects for backward compatibility.
 */
/**
 * Safely formats a date value for display.
 * Handles Firestore Timestamps, ISO strings, and null/undefined.
 */
const formatDate = (value, options = {}) => {
  if (!value) return "";
  let date;
  if (typeof value === "object" && typeof value.toMillis === "function") {
    date = new Date(value.toMillis());
  } else if (typeof value === "object" && typeof value.seconds === "number") {
    date = new Date(value.seconds * 1000 + (value.nanoseconds || 0) / 1e6);
  } else {
    date = new Date(value);
  }
  if (isNaN(date.getTime())) return "";
  const defaults = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };
  return date.toLocaleDateString("en-US", { ...defaults, ...options });
};

const normalizeFirestoreData = (data) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) return data;
  const hasDottedKeys = Object.keys(data).some((key) => key.includes("."));
  if (!hasDottedKeys) return data;
  const result = {};
  Object.entries(data).forEach(([key, value]) => {
    if (key.includes(".")) {
      const parts = key.split(".");
      let current = result;
      for (let i = 0; i < parts.length - 1; i++) {
        if (current[parts[i]] == null || typeof current[parts[i]] !== "object") {
          current[parts[i]] = {};
        }
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = value;
    } else {
      result[key] = value;
    }
  });
  return result;
};

const QuestionForum = () => {
  const navigate = useNavigate();
  // const orgCode = 
  const orgCode =localStorage.getItem("orgCode");
  // const hardcodedStudentId =
  const hardcodedStudentId = localStorage.getItem("studentId");
  const [questions, setQuestions] = useState([]);
  const [questionText, setQuestionText] = useState("");
  const [category, setCategory] = useState("General");
  const [expandedId, setExpandedId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState("all");
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("ask");
  const [editorFocused, setEditorFocused] = useState(false);

  // Fetch questions from Firebase on mount
  useEffect(() => {
    fetchQuestions();
  }, [orgCode]);

  // Fetch courses from Firebase
  useEffect(() => {
    const fetchCourses = async () => {
      if (!orgCode) {
        setCoursesLoading(false);
        return;
      }
      try {
        const programInfoRef = collection(db, `CorporateClients/${orgCode}/programInfo`);
        const snapshot = await getDocs(programInfoRef);
        const courseList = [];
        snapshot.forEach((doc) => {
          const courseName = doc.id;
          if (!/capstone/i.test(courseName)) {
            courseList.push(courseName);
          }
        });
        setCourses(courseList.sort());
      } catch (err) {
        console.error("Failed to fetch courses:", err);
      } finally {
        setCoursesLoading(false);
      }
    };
    fetchCourses();
  }, [orgCode]);

  // Fetch questions from Firebase
  // Supports TWO formats:
  //   NEW: course subcollection — questionanswer/{studentId}/{courseName}/{questionId}
  //   LEGACY: flat doc — questionanswer/{studentId} { courses: { Course01: { questions: { qId: {...} } } } }
  const fetchQuestions = async () => {
    if (!orgCode) return;
    try {
      const allQuestions = [];
      const loadedIds = new Set();

      // First, get course names from programInfo (for new-format querying)
      let knownCourses = ["General"];
      try {
        const programInfoRef = collection(db, `CorporateClients/${orgCode}/programInfo`);
        const programSnapshot = await getDocs(programInfoRef);
        const programCourses = [];
        programSnapshot.forEach((d) => {
          if (!/capstone/i.test(d.id)) programCourses.push(d.id);
        });
        knownCourses = [...knownCourses, ...programCourses];
      } catch {
        // programInfo may not exist — use just "General"
      }

      const studentMap = {};

      // ── Populate studentMap with all students from studentInfo ──
      try {
        const studentsRef = collection(db, `CorporateClients/${orgCode}/studentInfo`);
        const studentsSnapshot = await getDocs(studentsRef);
        studentsSnapshot.forEach((sDoc) => {
          const sid = sDoc.id;
          const studentName = sDoc.data().fullName || sid;
          studentMap[sid] = { studentName, courseNames: new Set() };
        });
      } catch (err) {
        console.warn("Failed to fetch students from studentInfo:", err);
      }

      // ── Read all student docs from questionanswer (covers legacy data) ──
      const qaRef = collection(db, `CorporateClients/${orgCode}/questionanswer`);
      const qaSnapshot = await getDocs(qaRef);

      qaSnapshot.forEach((studentDoc) => {
        const sid = studentDoc.id;
        const rawData = studentDoc.data();
        const data = normalizeFirestoreData(rawData);
        const studentName = data.studentName || (studentMap[sid]?.studentName) || sid;

        if (!studentMap[sid]) {
          studentMap[sid] = { studentName, courseNames: new Set() };
        }

        // ── LEGACY: parse nested courses.questions map ──
        if (data.courses) {
          Object.entries(data.courses).forEach(([courseName, courseData]) => {
            studentMap[sid].courseNames.add(courseName);
            if (courseData && courseData.questions) {
              Object.entries(courseData.questions).forEach(([qId, qData]) => {
                if (!loadedIds.has(qId)) {
                  loadedIds.add(qId);
                  allQuestions.push({
                    id: qId,
                    studentId: sid,
                    studentName,
                    category: courseName,
                    ...qData,
                  });
                }
              });
            }
          });
        }
      });

      // ── NEW FORMAT: query each course subcollection for each student ──
      const subcollectionPromises = [];
      Object.entries(studentMap).forEach(([sid, info]) => {
        const coursesToCheck = new Set([...info.courseNames, ...knownCourses]);
        coursesToCheck.forEach((courseName) => {
          const questionsRef = collection(
            db,
            `CorporateClients/${orgCode}/questionanswer/${sid}/${courseName}`
          );
          subcollectionPromises.push(
            getDocs(questionsRef).then((snapshot) => {
              snapshot.forEach((qDoc) => {
                const data = qDoc.data();
                const qId = qDoc.id;
                if (!loadedIds.has(qId)) {
                  loadedIds.add(qId);
                  allQuestions.push({
                    id: qId,
                    studentId: sid,
                    studentName: info.studentName,
                    category: courseName,
                    question: data.question || "",
                    answer: data.answer || null,
                    answeredBy: data.answeredBy || null,
                    answeredAt: data.answeredAt || null,
                    createdAt: data.createdAt || null,
                    status: data.status || "pending",
                  });
                }
              });
            })
          );
        });
      });

      await Promise.all(subcollectionPromises);

      // Sort by createdAt descending
      allQuestions.sort((a, b) => {
        const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : new Date(a.createdAt || 0).getTime();
        const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : new Date(b.createdAt || 0).getTime();
        return bTime - aTime;
      });

      setQuestions(allQuestions);
    } catch (err) {
      console.error("Failed to fetch questions:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isEmptyHtml(questionText) || !hardcodedStudentId) return;

    setSubmitting(true);

    const studentName = hardcodedStudentId;

    // ── NEW FORMAT: save as a document in the course subcollection ──
    // Path: questionanswer/{selectedStudent}/{category}/{autoId}
    // The course name (category) IS the subcollection name
    const courseQuestionsRef = collection(
      db,
      `CorporateClients/${orgCode}/questionanswer/${hardcodedStudentId}/${category}`
    );
    const questionDocRef = doc(courseQuestionsRef); // auto-generate ID

    const questionData = {
      question: questionText.trim(),
      answer: null,
      answeredBy: null,
      answeredAt: null,
      createdAt: serverTimestamp(),
      status: "pending",
    };

    try {
      await setDoc(questionDocRef, questionData);

      const displayQ = {
        id: questionDocRef.id,
        studentId: hardcodedStudentId,
        studentName,
        category,
        ...questionData,
        createdAt: new Date().toISOString(),
      };

      setQuestions([displayQ, ...questions]);
      setQuestionText("");
    } catch (err) {
      console.error("Failed to save question:", err);
    } finally {
      setSubmitting(false);
    }
  };

  // Filtering logic
  // Step 1: Filter by hardcoded student only (all questions regardless of course)
  const studentQuestions = questions.filter((q) => {
    if (hardcodedStudentId && q.studentId !== hardcodedStudentId) return false;
    return true;
  });

  // Step 2: Apply status filter ("all", "pending", "answered") — independent of category
  const filteredQuestions = questions.filter((q) => {
    if (filter === "all") {
       // "All" filter displays answered questions from all students
      return q.status === "answered";
    }
    // Otherwise, only show the logged-in student's questions matching the status
    if (hardcodedStudentId && q.studentId !== hardcodedStudentId) return false;
    if (filter === "pending") return q.status === "pending";
    if (filter === "answered") return q.status === "answered";
    return true;
  });

  // Stats reflect the selected student's questions
  const answeredCount = studentQuestions.filter((q) => q.status === "answered").length;
  const pendingCount = studentQuestions.filter((q) => q.status === "pending").length;

  // "isAllTab" is now purely cosmetic — controls empty-state messaging
  const isAllTab = filter === "all";

  return (
    <div className="qf-page">
      {/* Header bar */}
      <div className="qf-header">
        <div className="qf-header-inner">
          <div className="qf-header-left">
            {/* <button
              onClick={() => navigate(`/${orgCode}`)}
              className="qf-back-btn"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button> */}
            <div className="qf-header-title-group">
              <h1>Q&A Forum</h1>
              <p>Ask questions, get answers from instructors</p>
            </div>
          </div>
          <div className="qf-header-right">
            {/* <div className="qf-header-student-info">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="18" height="18">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="qf-header-student-name">{hardcodedStudentId}</span>
            </div> */}
            <nav className="qf-header-nav">
              {/* <button
                onClick={() => navigate(`/${orgCode}`)}
                className="qf-nav-link"
              >
                Home
              </button> */}
            </nav>
          </div>
        </div>
      </div>

      <div className="qf-content">
        {/* Tab navigation */}
        <div className="qf-tabs">
          <button
            className={`qf-tab ${activeTab === "ask" ? "qf-tab-active" : ""}`}
            onClick={() => setActiveTab("ask")}
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="18" height="18">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Ask Question
          </button>
          <button
            className={`qf-tab ${activeTab === "all" ? "qf-tab-active" : ""}`}
            onClick={() => setActiveTab("all")}
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="18" height="18">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            All Asked Questions
          </button>
        </div>

        {/* Tab content */}
        {activeTab === "ask" && (
          <div className="qf-form-card">
            <h2 className="qf-form-title">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Ask a Question
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="qf-form-group">
                <label className="qf-form-label">Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="qf-form-select"
                >
                  <option value="General">General</option>
                  {coursesLoading ? (
                    <option disabled>Loading courses...</option>
                  ) : (
                    courses.map((course) => (
                      <option key={course} value={course}>
                        {course}
                      </option>
                    ))
                  )}
                </select>
              </div>

              <div className="qf-form-group">
                <label className="qf-form-label">Your Question</label>
                <div className={`qf-form-editor ${editorFocused ? "focused" : ""}`}>
                  <ReactQuill
                    theme="snow"
                    value={questionText}
                    onChange={setQuestionText}
                    onFocus={() => setEditorFocused(true)}
                    onBlur={() => setEditorFocused(false)}
                    placeholder="Type your question here..."
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={isEmptyHtml(questionText) || !hardcodedStudentId || submitting}
                className="qf-submit-btn"
              >
                {submitting ? "Posting..." : "Post Question"}
              </button>
            </form>
          </div>
        )}

        {activeTab === "all" && (
          <>
            {/* Stats & Filters */}
            <div className="qf-stats-bar">
              <div className="qf-stats">
                <span className="qf-stat-item">
                  <strong>{studentQuestions.length}</strong> total questions
                </span>
                <span className="qf-stat-item qf-stat-answered">
                  <strong>{answeredCount}</strong> answered
                </span>
                <span className="qf-stat-item qf-stat-pending">
                  <strong>{pendingCount}</strong> pending
                </span>
              </div>
              <div className="qf-filter-group">
                {["all", "pending", "answered"].map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`qf-filter-btn ${filter === f ? "qf-filter-active" : ""}`}
                  >
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Questions */}
            {filteredQuestions.length === 0 ? (
              <div className="qf-empty-state">
                <svg className="qf-empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <p className="qf-empty-title">
                  {isAllTab
                    ? `No questions for ${hardcodedStudentId || "this student"}`
                    : studentQuestions.length === 0
                      ? `No questions for ${hardcodedStudentId || "this student"}`
                      : `No ${filter} questions for ${hardcodedStudentId || "this student"}`}
                </p>
                <p className="qf-empty-subtitle">
                  {(isAllTab || studentQuestions.length === 0) && "Use the Ask Question tab to ask the first question!"}
                </p>
              </div>
            ) : (
              <div className="qf-questions-list">
                {filteredQuestions.map((q) => (
                  <div
                    key={q.id}
                    className="qf-question-card"
                  >
                    {/* Question Header */}
                    <div
                      onClick={() =>
                        setExpandedId(expandedId === q.id ? null : q.id)
                      }
                      className="qf-question-header"
                    >
                      <div className="qf-question-top">
                        <div className="qf-question-body">
                          <div className="qf-badges">
                            <span
                              className={`qf-badge ${q.status === "answered"
                                ? "qf-badge-answered"
                                : "qf-badge-pending"
                                }`}
                            >
                              {q.status === "answered" ? "Answered" : "Pending"}
                            </span>
                            <span className="qf-badge qf-badge-category">
                              {q.category}
                            </span>
                          </div>
                          <div 
                            className="qf-question-text qf-rich-text"
                            dangerouslySetInnerHTML={{ __html: q.question }}
                          />
                          <div className="qf-question-meta">
                            <div className="qf-meta-avatar">
                              {(q.studentName || q.userName || "?").charAt(0).toUpperCase()}
                            </div>
                            <span className="qf-meta-name">{q.studentName || q.userName || "Anonymous"}</span>
                            <span className="qf-meta-date">
                              &middot;{" "}
                              {formatDate(q.createdAt)}
                            </span>
                          </div>
                        </div>
                        <svg
                          className={`qf-expand-icon ${expandedId === q.id ? "qf-expanded" : ""}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>

                    {/* Answer Section (expandable) */}
                    {expandedId === q.id && (
                      <div className="qf-answer-section">
                        {q.answer ? (
                          <div className="qf-answer-content">
                            <div className="qf-answer-inner">
                              <div className="qf-answer-avatar">
                                {q.answeredBy?.charAt(0).toUpperCase() || "A"}
                              </div>
                              <div className="qf-answer-body">
                                <div className="qf-answer-meta">
                                  <span className="qf-answer-author">
                                    {q.answeredBy || "Instructor"}
                                  </span>
                                  <span className="qf-answer-role">(Instructor)</span>
                                  <span className="qf-answer-date">
                                    &middot;{" "}
                                    {formatDate(q.answeredAt, { year: "numeric", month: "short", day: "numeric" })}
                                  </span>
                                </div>
                                <div className="qf-answer-text">
                                  {q.answer}
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="qf-no-answer">
                            <svg className="qf-no-answer-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="qf-no-answer-text">
                              Waiting for an instructor to answer...
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default QuestionForum;
